import { useCallback, useEffect, useMemo, useState } from 'react'
import { BufferGeometry, Mesh, Vector3 } from 'three'
import { useThree } from '@react-three/fiber'

export const ADD_CALLOUT_SELECTION_KEY = 'add-comment-callout'
export const ADD_KNEE_SELECTION_KEY = 'add-comment-callout-knee'

interface Params {
  comments: Comment3D[]
  /** The comment whose callouts are being edited (the displayed selection). */
  activeComment: Comment3D | null
  isOrthographic: boolean
  selectionModeKey: string | null
  updateCommentFields: (id: string, changes: Partial<Comment3D>) => void
  setSelectionMode: (config: { key: string; message: string }) => void
  unsetSelectionMode: () => void
}

/**
 * Callout (and knee) editing for the active comment: drawing a new callout via
 * the `add-comment-callout` selection mode, placing/removing a knee, and the
 * hover/select/delete state for the interactive callout overlay.
 */
const useCommentCallouts = ({
  comments,
  activeComment,
  isOrthographic,
  selectionModeKey,
  updateCommentFields,
  setSelectionMode,
  unsetSelectionMode,
}: Params) => {
  const gl = useThree(state => state.gl)

  // The comment a callout is currently being drawn for (via the HUD button).
  const [calloutCommentId, setCalloutCommentId] = useState<string | null>(null)
  // The callout a knee point is currently being placed for.
  const [kneeTarget, setKneeTarget] = useState<{ commentId: string; index: number } | null>(null)
  // Selected / hovered existing callout (index) of the active comment.
  const [selectedCalloutIndex, setSelectedCalloutIndex] = useState<number | null>(null)
  const [hoveredCalloutIndex, setHoveredCalloutIndex] = useState<number | null>(null)

  const isCalloutMode = selectionModeKey === ADD_CALLOUT_SELECTION_KEY
  const isKneeMode = selectionModeKey === ADD_KNEE_SELECTION_KEY

  const calloutComment = useMemo(
    () =>
      calloutCommentId ? comments.find(comment => comment.id === calloutCommentId) ?? null : null,
    [comments, calloutCommentId],
  )

  // Expose the active comment's center as a snap target while drawing a callout,
  // so the user can snap the callout end to the comment itself. In floorplan
  // (xyOnly) the draw plane is z=0, so the target's z must be zeroed too –
  // matching useCommentSnapTargets – otherwise it never snaps.
  const calloutSnapTargets = useMemo(() => {
    if (!calloutComment) return undefined
    const center = new Vector3(
      calloutComment.position.x,
      calloutComment.position.y,
      isOrthographic ? 0 : calloutComment.position.z,
    )
    return [new Mesh(new BufferGeometry().setFromPoints([center]))]
  }, [calloutComment, isOrthographic])

  useEffect(() => {
    return () => {
      calloutSnapTargets?.forEach(mesh => mesh.geometry.dispose())
    }
  }, [calloutSnapTargets])

  const startCallout = useCallback(
    (comment: Comment3D) => {
      setCalloutCommentId(comment.id)
      setSelectionMode({
        key: ADD_CALLOUT_SELECTION_KEY,
        message: 'Endpunkt für den Verweispfeil anwählen',
      })
    },
    [setSelectionMode],
  )

  const handleAddCalloutPoint = useCallback(
    (point: Vector3) => {
      if (calloutComment) {
        // In floorplan (xyOnly) the picked z is the draw plane, not the comment's
        // plane – keep the callout end co-planar with the comment in that case.
        const z = isOrthographic ? calloutComment.position.z : point.z
        const callouts = [
          ...(calloutComment.callouts ?? []),
          { end: { x: point.x, y: point.y, z } as Point, knee: null },
        ]
        updateCommentFields(calloutComment.id, { callouts })
      }
      setCalloutCommentId(null)
      unsetSelectionMode()
    },
    [calloutComment, isOrthographic, updateCommentFields, unsetSelectionMode],
  )

  const handleDeleteCallout = useCallback(
    (index: number) => {
      if (!activeComment) return
      const remaining = (activeComment.callouts ?? []).filter((_, i) => i !== index)
      updateCommentFields(activeComment.id, {
        callouts: remaining.length > 0 ? remaining : null,
      })
      setSelectedCalloutIndex(null)
    },
    [activeComment, updateCommentFields],
  )

  const handleRemoveKnee = useCallback(
    (index: number) => {
      if (!activeComment) return
      const callouts = (activeComment.callouts ?? []).map((callout, i) =>
        i === index ? { ...callout, knee: null } : callout,
      )
      updateCommentFields(activeComment.id, { callouts })
    },
    [activeComment, updateCommentFields],
  )

  const handleHoverCallout = useCallback(
    (index: number | null) => {
      setHoveredCalloutIndex(index)
      gl.domElement.style.cursor = index !== null ? 'pointer' : 'auto'
    },
    [gl],
  )

  const startKnee = useCallback(
    (index: number) => {
      if (!activeComment) return
      setKneeTarget({ commentId: activeComment.id, index })
      setSelectionMode({
        key: ADD_KNEE_SELECTION_KEY,
        message: 'Kniepunkt für den Verweispfeil anwählen',
      })
    },
    [activeComment, setSelectionMode],
  )

  const handleAddKneePoint = useCallback(
    (point: Vector3) => {
      const comment = kneeTarget ? comments.find(item => item.id === kneeTarget.commentId) : null
      if (kneeTarget && comment?.callouts?.[kneeTarget.index]) {
        // In floorplan (xyOnly) the picked z is the draw plane – keep the knee
        // co-planar with the comment in that case (matching the callout end).
        const z = isOrthographic ? comment.position.z : point.z
        const callouts = comment.callouts.map((callout, i) =>
          i === kneeTarget.index
            ? { ...callout, knee: { x: point.x, y: point.y, z } as Point }
            : callout,
        )
        updateCommentFields(comment.id, { callouts })
      }
      setKneeTarget(null)
      unsetSelectionMode()
    },
    [kneeTarget, comments, isOrthographic, updateCommentFields, unsetSelectionMode],
  )

  // Reset callout selection/hover when the active comment changes.
  const activeCommentId = activeComment?.id ?? null
  useEffect(() => {
    setSelectedCalloutIndex(null)
    setHoveredCalloutIndex(null)
  }, [activeCommentId])

  // Cancel any in-progress callout/knee drawing (e.g. on Escape).
  const cancelDrawing = useCallback(() => {
    setCalloutCommentId(null)
    setKneeTarget(null)
    unsetSelectionMode()
  }, [unsetSelectionMode])

  return {
    isCalloutMode,
    isKneeMode,
    calloutSnapTargets,
    startCallout,
    handleAddCalloutPoint,
    handleAddKneePoint,
    selectedCalloutIndex,
    setSelectedCalloutIndex,
    hoveredCalloutIndex,
    handleHoverCallout,
    handleDeleteCallout,
    startKnee,
    handleRemoveKnee,
    cancelDrawing,
  }
}

export default useCommentCallouts
